import { useCallback, useEffect, useRef, useState } from 'react'

export interface Loaded<T> {
  data: T | undefined
  error: Error | undefined
  loading: boolean
  reload: () => void
  set: (data: T) => void
}

/** Load once per key and on reload. Keeps the last data while reloading, so lists don't flash. */
export function useLoad<T>(fn: () => Promise<T>, key: unknown[]): Loaded<T> {
  const [data, setData] = useState<T>()
  const [error, setError] = useState<Error>()
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)
  const fnRef = useRef(fn)
  fnRef.current = fn

  useEffect(() => {
    let live = true
    setLoading(true)
    fnRef.current().then(
      (d) => live && (setData(d), setError(undefined), setLoading(false)),
      (e: unknown) => live && (setError(e instanceof Error ? e : new Error(String(e))), setLoading(false)),
    )
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...key, tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { data, error, loading, reload, set: setData }
}
